import { Link } from "react-router-dom";
import { Phone, Shield, Heart, MapPin, ChevronRight, Check, Star, Quote } from "lucide-react";

const services = [
  {
    name: "Raccoons",
    image: "https://images.unsplash.com/photo-1682627101217-32899f5800a0?w=600",
    description: "Safe removal from attics and structures"
  },
  {
    name: "Squirrels and Chipmunks",
    image: "https://images.unsplash.com/photo-1615125965946-9a5ca7a488dc?w=600",
    description: "Expert exclusion services"
  },
  {
    name: "Rats and Mice",
    image: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=600",
    description: "Comprehensive rodent control"
  },
  {
    name: "Skunks",
    image: "https://images.unsplash.com/photo-1561493668-d4a914593d45?w=600",
    description: "Careful, odor-free removal"
  },
  {
    name: "Snakes",
    image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/99zzox2n_IMG_20250817_140355_1~2.jpg",
    description: "Safe capture and relocation"
  },
  {
    name: "Birds and Bats",
    image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/2h668yqq_images.jpeg",
    description: "Safe exclusion and prevention"
  },
  {
    name: "Attic Cleanup",
    image: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/52xuf341_unnamed.jpg",
    description: "Contaminated insulation removal"
  }
];

const serviceAreas = [
  "Prince Edward County",
  "Belleville",
  "Trenton",
  "Brighton",
  "Tweed",
  "Napanee"
];

const HomePage = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-primary overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 texture-overlay pointer-events-none"></div>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
            {/* Left - Content */}
            <div className="flex flex-col justify-center px-6 lg:px-12 py-16 lg:py-24">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-accent" />
                <span className="text-primary-foreground/80 font-body text-sm uppercase tracking-wider">
                  Licensed and Insured
                </span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-primary-foreground uppercase tracking-tight leading-tight mb-6">
                Prevent House Fires.<br />
                <span className="text-accent">Catch and Patch.</span>
              </h1>
              <p className="text-primary-foreground/80 text-lg mb-8 font-body max-w-lg">
                Professional wildlife control using humane live trapping methods. 
                Protecting your home and respecting nature since day one.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-sm font-heading text-sm uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
                  data-testid="hero-cta-quote"
                >
                  Get Free Quote
                  <ChevronRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:343-263-2000"
                  className="inline-flex items-center justify-center gap-2 border-2 border-primary-foreground text-primary-foreground px-6 py-3 rounded-sm font-heading text-sm uppercase tracking-wide transition-all hover:bg-primary-foreground hover:text-primary"
                  data-testid="hero-cta-call"
                >
                  <Phone className="h-4 w-4" />
                  Call Now
                </a>
              </div>
              
              {/* Mobile Hero Image */}
              <div className="lg:hidden mt-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/99zzox2n_IMG_20250817_140355_1~2.jpg"
                  alt="Critter Whisperer Charlie handling a large snake"
                  className="w-full h-64 object-cover object-top rounded-sm border-2 border-primary-foreground/20"
                />
              </div>
            </div>
            {/* Right - Image (Desktop) */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 hero-clip">
                <img
                  src="https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/99zzox2n_IMG_20250817_140355_1~2.jpg"
                  alt="Critter Whisperer Charlie handling a large snake"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-secondary/10 py-6" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-foreground/80">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Fully Licensed</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <Check className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <Heart className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Humane Methods</span>
            </div>
            <div className="flex items-center gap-2 text-foreground/80">
              <MapPin className="h-5 w-5 text-primary" />
              <span className="font-heading text-sm uppercase tracking-wide">Local Expert</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 lg:py-24 px-4" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-4">
              Our Services
            </h2>
            <p className="text-muted-foreground font-body max-w-2xl">
              From raccoons in your attic to contaminated insulation cleanup, we handle all your wildlife control needs 
              with professional care and humane practices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.name}
                className="service-card bg-card border-2 border-secondary/20 rounded-sm overflow-hidden hover:border-accent transition-all group"
                data-testid={`service-card-${index}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg uppercase text-foreground mb-2">
                    {service.name}
                  </h3>
                  <p className="text-muted-foreground font-body text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm font-heading text-sm uppercase tracking-wide rugged-shadow transition-all hover:bg-primary/90"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-16 lg:py-24 bg-primary/5" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-6">
                Why Choose Critter Whisperer Charlie?
              </h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-foreground">Humane Live Trapping</span>
                    <p className="text-muted-foreground font-body text-sm">We prioritize the welfare of animals while protecting your property.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-foreground">Licensed and Insured</span>
                    <p className="text-muted-foreground font-body text-sm">Full coverage for your peace of mind.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-foreground">Catch and Patch</span>
                    <p className="text-muted-foreground font-body text-sm">We do not just remove - we seal entry points to prevent future problems.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-foreground">Local Expertise</span>
                    <p className="text-muted-foreground font-body text-sm">We know the wildlife of Ontario and how to handle them effectively.</p>
                  </div>
                </li>
              </ul>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-primary font-heading uppercase tracking-wide hover:text-accent transition-colors"
                data-testid="learn-more-btn"
              >
                Learn More About Us
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1688224676400-e9c37860f81d?w=600"
                alt="Raccoon on wooden structure"
                className="rounded-sm border-2 border-secondary/20 rugged-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 px-4" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-4">
              What Our Customers Say
            </h2>
            <p className="text-muted-foreground font-body">
              Real feedback from satisfied homeowners across Eastern Ontario
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm relative" data-testid="testimonial-1">
              <Quote className="h-8 w-8 text-accent/20 absolute top-4 right-4" />
              <div className="flex gap-1 mb-4">
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
              </div>
              <p className="text-foreground font-body mb-4 italic">
                "Charlie Patrick is a wizard!"
              </p>
              <div className="border-t border-secondary/20 pt-4">
                <p className="font-heading font-bold text-foreground">Lindsay V.</p>
                <p className="text-muted-foreground text-sm font-body">Facebook Review</p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm relative" data-testid="testimonial-2">
              <Quote className="h-8 w-8 text-accent/20 absolute top-4 right-4" />
              <div className="flex gap-1 mb-4">
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
              </div>
              <p className="text-foreground font-body mb-4 italic">
                "Charlie Patrick is the best!"
              </p>
              <div className="border-t border-secondary/20 pt-4">
                <p className="font-heading font-bold text-foreground">Jennifer P.</p>
                <p className="text-muted-foreground text-sm font-body">Facebook Review</p>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm relative" data-testid="testimonial-3">
              <Quote className="h-8 w-8 text-accent/20 absolute top-4 right-4" />
              <div className="flex gap-1 mb-4">
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
              </div>
              <p className="text-foreground font-body mb-4 italic">
                "Charlie Patrick for sure"
              </p>
              <div className="border-t border-secondary/20 pt-4">
                <p className="font-heading font-bold text-foreground">Lisa Marie L.</p>
                <p className="text-muted-foreground text-sm font-body">Facebook Review</p>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm relative" data-testid="testimonial-4">
              <Quote className="h-8 w-8 text-accent/20 absolute top-4 right-4" />
              <div className="flex gap-1 mb-4">
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
              </div>
              <p className="text-foreground font-body mb-4 italic">
                "Charlie Patrick is your man"
              </p>
              <div className="border-t border-secondary/20 pt-4">
                <p className="font-heading font-bold text-foreground">Laurie N.</p>
                <p className="text-muted-foreground text-sm font-body">Facebook Review</p>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-card border-2 border-secondary/20 p-6 rounded-sm relative" data-testid="testimonial-5">
              <Quote className="h-8 w-8 text-accent/20 absolute top-4 right-4" />
              <div className="flex gap-1 mb-4">
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
                <Star className="h-5 w-5 fill-accent text-accent" />
              </div>
              <p className="text-foreground font-body mb-4 italic">
                "Thank you to Charlie for your prompt assistance with our raccoon problem. We appreciated your prompt professional approach to handling this concern and removing them safely from our rental property. You were easy to work with and helpful in identifying points of entry to control our situation. Thank you for being an amazing critter whisperer."
              </p>
              <div className="border-t border-secondary/20 pt-4">
                <p className="font-heading font-bold text-foreground">Sherry B.</p>
                <p className="text-muted-foreground text-sm font-body">Raccoon Removal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 lg:py-24 px-4" data-testid="service-areas-preview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-foreground mb-4">
              Service Areas
            </h2>
            <p className="text-muted-foreground font-body">
              Proudly serving communities across Eastern Ontario
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((area) => (
              <div
                key={area}
                className="flex items-center gap-2 bg-card border-2 border-secondary/20 px-5 py-3 rounded-sm"
                data-testid={`area-badge-${area.toLowerCase().replace(" ", "-")}`}
              >
                <MapPin className="h-4 w-4 text-accent" />
                <span className="font-heading text-sm uppercase tracking-wide text-foreground">{area}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 text-primary font-heading uppercase tracking-wide hover:text-accent transition-colors"
              data-testid="view-all-areas-btn"
            >
              View All Service Areas
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary-foreground mb-6">
            Wildlife Problem? We Can Help.
          </h2>
          <p className="text-primary-foreground/80 font-body text-lg mb-8">
            Do not let uninvited guests cause damage to your home. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
              data-testid="cta-get-quote"
            >
              Get Your Free Quote
              <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:343-263-2000"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-foreground text-primary-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide transition-all hover:bg-primary-foreground hover:text-primary"
              data-testid="cta-call"
            >
              <Phone className="h-4 w-4" />
              343-263-2000
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
